/**
 * Who has been given this lot, and giving it to one more.
 *
 * WHAT THIS PANE IS FOR. Everything else on a lot record is internal: the ledger, the genealogy,
 * the certificates attached to it. This is the one place where any of it leaves the building, and
 * it is the manufacturer's half of the customer portal -- what a contact sees at
 * customer.sbom.cres-intelligence.io is exactly what was released here.
 *
 * IT IS ALSO THE RECALL QUERY. "Which customers hold batch 24-0117" has no other answer in this
 * estate, which is why the list is the pane and the dialog is the button on it rather than the
 * other way round.
 *
 * THE FILES ARE TICKED ONE BY ONE, AND THE DIALOG SAYS SO IN WORDS. A lot record accumulates
 * internal documents -- a deviation, an inspection report, a photograph of a damaged pallet -- and
 * a release that meant "this lot's attachments" would publish every one of them, including the
 * ones attached next month, to a customer, with nobody deciding to. The cost of naming them is
 * that a corrected certificate attached later is not shared until somebody releases it again, and
 * the caption says that too: a releaser who does not know it would assume the opposite.
 *
 * WHAT IS NOT OFFERED ANYWHERE ON THIS PANE: a way to disclose a supplier. Genealogy is a tick box
 * and it shares lot numbers; `Received From` is not walked by the service under any flag, and
 * there is no control here that would ask it to.
 *
 * THE BUTTON IS HIDDEN WITHOUT THE PRIVILEGE, not disabled and not left to fail at the server.
 * Releasing is `inventory:releaseToCustomer` and reading this list is
 * `inventory:viewCustomerReleases` -- two authorities on purpose, because a complaint
 * investigation needs to know who holds a batch and has no business publishing one. Both are
 * enforced in the service; this only avoids offering a decision somebody cannot take.
 */
import React, {useCallback, useContext, useEffect, useState} from "react";
import {
    Autocomplete, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent,
    DialogTitle, Divider, FormControlLabel, TextField, Typography,
} from "@mui/material";

import {AppContext} from "../../util/AppContext";
import {makeCresAxios} from "../../util/Axios";
import {Grey2, Grey5, Grey7, Grey8, Grey17} from "../../theme/styles";

const RELEASE_PRIVILEGE = "inventory:releaseToCustomer";

type Release = {
    USID: string;
    RecipientName?: string | null;
    RecipientEmail?: string | null;
    Quantity?: number | string | null;
    UnitOfMeasure?: string | null;
    ShipmentReference?: string | null;
    ShippedDatetime?: string | null;
    ReleasedDatetime?: string | null;
    ExpiresDatetime?: string | null;
    RevokedDatetime?: string | null;
    RevokedReason?: string | null;
    DiscloseGenealogy?: boolean;
    DocumentCount?: number | null;
    LastViewedDatetime?: string | null;
};

type Recipient = {
    USID: string; Name?: string | null; Email?: string | null; CompanyName?: string | null;
    Enabled?: boolean; Locked?: boolean; HasSignedIn?: boolean;
};

type Attachment = {
    AttachmentUSID: string; Name?: string | null; Bytes?: number | null; Missing?: boolean;
};

function when(value?: string | null): string {
    if (!value) return "—";
    const at = new Date(String(value).replace(" ", "T"));
    if (Number.isNaN(at.getTime())) return String(value).slice(0, 10);
    return at.toLocaleDateString(undefined, {day: "numeric", month: "short", year: "numeric"});
}

function size(bytes?: number | null): string {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** The quantity, or nothing. NEVER a bare number: "240" is 240 of something unstated. */
function amount(one: {Quantity?: number | string | null; UnitOfMeasure?: string | null}) {
    if (one.Quantity === null || one.Quantity === undefined || !one.UnitOfMeasure) return null;
    return `${one.Quantity} ${one.UnitOfMeasure}`;
}

export default function CustomerReleasesPane(
    {lotObjectUSID, revisionUSID, lotName}:
    {lotObjectUSID?: string | null; revisionUSID?: string | null; lotName?: string | null}) {

    const appContext: any = useContext(AppContext);
    const sessionContext = appContext?.sessionContext;
    const api = makeCresAxios(sessionContext);

    const [releases, setReleases] = useState<Release[] | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showWithdrawn, setShowWithdrawn] = useState(false);
    const [mayRelease, setMayRelease] = useState(false);
    const [busy, setBusy] = useState<string | null>(null);

    const load = useCallback(() => {
        if (!lotObjectUSID) return;
        setLoading(true);
        api.get("t1/v1/lot_releases",
                {params: {LotObjectUSID: lotObjectUSID, IncludeRevoked: showWithdrawn || undefined}})
            .then((response: any) => {
                setReleases(response?.data?.Results ?? []);
                setError(null);
            })
            // THE PLATFORM PUTS THE HUMAN SENTENCE IN Cause. Without it this could only ever say
            // "could not be listed", which is the shrug that hides a plain privilege refusal --
            // and a refusal here is actionable: ask for inventory:viewCustomerReleases.
            .catch((problem: any) => {
                setReleases(null);
                setError(problem?.response?.data?.Cause
                         ?? "This lot's customer releases could not be listed.");
            })
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lotObjectUSID, showWithdrawn]);

    useEffect(() => { load(); }, [load]);

    // WHETHER TO OFFER THE BUTTON AT ALL, asked of the record rather than of the session: a
    // per-share grant or a deny narrows privileges for ONE lot, and a global list would say yes
    // where the server will say no -- which is worse than not offering it, because the person
    // fills the dialog in first.
    useEffect(() => {
        if (!revisionUSID) return;
        let cancelled = false;
        api.get("t1/v1/object_access", {params: {USID: revisionUSID}})
            .then((response: any) => {
                if (cancelled) return;
                const granted: string[] = response?.data?.EffectivePrivileges ?? [];
                setMayRelease(granted.includes(RELEASE_PRIVILEGE));
            })
            .catch(() => { if (!cancelled) setMayRelease(false); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [revisionUSID]);

    const [dialogOpen, setDialogOpen] = useState(false);

    const withdraw = async (release: Release) => {
        setBusy(release.USID);
        try {
            await api.delete("t1/v1/lot_releases", {params: {USID: release.USID}});
            load();
        } catch (problem: any) {
            setError(problem?.response?.data?.Cause ?? "That release could not be withdrawn.");
        } finally {
            setBusy(null);
        }
    };

    if (loading && releases === null && !error) {
        return <Box sx={{display: "flex", gap: "10px", alignItems: "center", padding: "20px 0"}}>
            <CircularProgress size={18}/>
            <Typography sx={{fontSize: 13, color: Grey7}}>Reading the release record…</Typography>
        </Box>;
    }

    const live = (releases ?? []).filter(one => !one.RevokedDatetime);

    return (
        <Box sx={{paddingTop: "6px", maxWidth: 820}}>
            <Box sx={{display: "flex", alignItems: "center", gap: "12px", marginBottom: "10px"}}>
                <Typography sx={{fontSize: 14, fontWeight: 600, color: Grey8}}>
                    Released to customers
                </Typography>
                <Box sx={{flex: 1}}/>
                {mayRelease && (
                    <Button size="small" variant="outlined" onClick={() => setDialogOpen(true)}>
                        Release to a customer
                    </Button>
                )}
            </Box>

            {error && (
                <Typography sx={{fontSize: 13, color: "#B3261E", marginBottom: "10px"}}>
                    {error}
                </Typography>
            )}

            {!error && live.length === 0 && (
                <Typography sx={{fontSize: 13, color: Grey7, lineHeight: 1.6, maxWidth: 600}}>
                    No customer has been given this lot. Releasing it puts the batch, the documents
                    you choose and — if you say so — its genealogy in front of one named contact in
                    the customer portal, and records that you did.
                </Typography>
            )}

            {(releases ?? []).map(one => (
                <Box key={one.USID}
                     sx={{border: `1px solid ${Grey2}`, borderRadius: "6px", padding: "12px 14px",
                          marginBottom: "10px",
                          // A WITHDRAWN ROW IS GREYED, NOT HIDDEN once it is asked for: the whole
                          // point of keeping it is that it can be read.
                          opacity: one.RevokedDatetime ? 0.62 : 1}}>
                    <Box sx={{display: "flex", alignItems: "baseline", gap: "10px"}}>
                        <Typography sx={{fontSize: 13.5, fontWeight: 600, color: Grey17}}>
                            {one.RecipientName ?? "Unknown contact"}
                        </Typography>
                        <Typography sx={{fontSize: 12.5, color: Grey7}}>
                            {one.RecipientEmail}
                        </Typography>
                        <Box sx={{flex: 1}}/>
                        {!one.RevokedDatetime && mayRelease && (
                            <Button size="small" color="inherit" disabled={busy === one.USID}
                                    onClick={() => withdraw(one)}
                                    sx={{fontSize: 12, textTransform: "none", color: Grey7}}>
                                {busy === one.USID ? "Withdrawing…" : "Withdraw"}
                            </Button>
                        )}
                    </Box>

                    <Typography sx={{fontSize: 12.5, color: Grey7, marginTop: "4px"}}>
                        {amount(one) && `${amount(one)} · `}
                        {one.ShipmentReference && `${one.ShipmentReference} · `}
                        shipped {when(one.ShippedDatetime)}
                        {" · "}
                        {/* A COUNT, NEVER A RESTATEMENT OF IT. "Documents" against a zero reads as
                            a system that lost them; this says whose decision it was. */}
                        {one.DocumentCount
                            ? `${one.DocumentCount} document${one.DocumentCount === 1 ? "" : "s"}`
                            : "no documents released"}
                        {one.DiscloseGenealogy ? " · genealogy shared" : " · genealogy withheld"}
                    </Typography>

                    <Typography sx={{fontSize: 12, color: Grey5, marginTop: "4px"}}>
                        Released {when(one.ReleasedDatetime)}
                        {one.ExpiresDatetime
                            ? ` · access runs to ${when(one.ExpiresDatetime)}`
                            : " · access does not expire"}
                        {" · "}
                        {/* WHETHER THEY EVER OPENED IT, which is the question that follows "did we
                            send it" and the one a contract dispute actually turns on. */}
                        {one.LastViewedDatetime
                            ? `last opened ${when(one.LastViewedDatetime)}`
                            : "never opened"}
                    </Typography>

                    {one.RevokedDatetime && (
                        <Typography sx={{fontSize: 12, color: "#B54708", marginTop: "4px"}}>
                            Withdrawn {when(one.RevokedDatetime)}
                            {one.RevokedReason ? ` — ${one.RevokedReason}` : ""}
                        </Typography>
                    )}
                </Box>
            ))}

            <FormControlLabel
                sx={{marginTop: "4px"}}
                control={<Checkbox size="small" checked={showWithdrawn}
                                   onChange={event => setShowWithdrawn(event.target.checked)}/>}
                label={<Typography sx={{fontSize: 12.5, color: Grey7}}>
                    Show withdrawn releases
                </Typography>}/>

            <Typography sx={{fontSize: 12, color: Grey5, marginTop: "10px", lineHeight: 1.6,
                             maxWidth: 640}}>
                A release is a record of disclosure, not a stock movement — issuing the material is
                a separate entry on the ledger. Withdrawing one ends a contact's access; it does not
                unsend anything they have already downloaded.
            </Typography>

            {dialogOpen && (
                <ReleaseDialog lotObjectUSID={lotObjectUSID!} lotName={lotName}
                               onClose={() => setDialogOpen(false)}
                               onReleased={() => { setDialogOpen(false); load(); }}/>
            )}
        </Box>
    );
}

/**
 * The dialog. Three things to choose and two of them have consequences worth a sentence.
 *
 * EVERY CAPTION HERE IS LOAD-BEARING. The person using it decides what a customer sees, once, in a
 * hurry, and will not read a manual first -- so what a tick actually does is written beside it.
 */
function ReleaseDialog({lotObjectUSID, lotName, onClose, onReleased}: {
    lotObjectUSID: string; lotName?: string | null;
    onClose: () => void; onReleased: () => void;
}) {
    const appContext: any = useContext(AppContext);
    const api = makeCresAxios(appContext?.sessionContext);

    const [recipients, setRecipients] = useState<Recipient[]>([]);
    const [recipient, setRecipient] = useState<Recipient | null>(null);
    const [attachments, setAttachments] = useState<Attachment[]>([]);
    const [chosen, setChosen] = useState<Record<string, boolean>>({});
    const [quantity, setQuantity] = useState("");
    const [unit, setUnit] = useState("");
    const [reference, setReference] = useState("");
    const [shipped, setShipped] = useState("");
    const [genealogy, setGenealogy] = useState(false);
    const [neverExpires, setNeverExpires] = useState(false);
    const [expires, setExpires] = useState("");
    const [saving, setSaving] = useState(false);
    const [problem, setProblem] = useState<string | null>(null);

    useEffect(() => {
        api.get("t1/v1/lot_release_recipients")
            .then((response: any) => setRecipients(response?.data?.Results ?? []))
            .catch((failure: any) => setProblem(
                failure?.response?.data?.Cause ?? "The customer contacts could not be listed."));
        api.get("t1/v1/lot_release_attachments", {params: {LotObjectUSID: lotObjectUSID}})
            .then((response: any) => setAttachments(response?.data?.Results ?? []))
            .catch(() => setAttachments([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lotObjectUSID]);

    const release = async () => {
        setSaving(true);
        setProblem(null);
        try {
            await api.post("t1/v1/lot_releases", {
                LotObjectUSID: lotObjectUSID,
                RecipientUSID: recipient?.USID,
                Quantity: quantity.trim() === "" ? undefined : quantity.trim(),
                UnitOfMeasure: unit.trim() === "" ? undefined : unit.trim(),
                ShipmentReference: reference.trim() === "" ? undefined : reference.trim(),
                ShippedDatetime: shipped === "" ? undefined : shipped,
                DiscloseGenealogy: genealogy,
                Documents: attachments.filter(one => chosen[one.AttachmentUSID])
                                      .map(one => ({AttachmentUSID: one.AttachmentUSID})),
                // NEVER HAS TO BE ASKED FOR. Leaving the field alone gets the default window, so
                // "no expiry" is a decision somebody took rather than one that happened to them.
                ExpiresDatetime: neverExpires || expires === "" ? undefined : expires,
                Months: neverExpires ? 0 : undefined,
            });
            onReleased();
        } catch (failure: any) {
            // THE SERVER'S OWN SENTENCE, which names the thing that was wrong -- an unknown unit,
            // a file this lot does not carry, a contact at another account. Replacing it with
            // "that did not work" would throw away the only actionable half.
            setProblem(failure?.response?.data?.Cause ?? "That release could not be made.");
            setSaving(false);
        }
    };

    return (
        <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{fontSize: 16}}>
                Release {lotName ? `“${lotName}”` : "this lot"} to a customer
            </DialogTitle>
            <DialogContent dividers>
                <Autocomplete
                    options={recipients}
                    value={recipient}
                    onChange={(_event, value) => setRecipient(value)}
                    getOptionLabel={one => `${one.Name ?? ""} (${one.Email ?? ""})`}
                    isOptionEqualToValue={(one, other) => one.USID === other.USID}
                    renderOption={(props, one) => (
                        <Box component="li" {...props} key={one.USID}
                             sx={{display: "block !important"}}>
                            <Typography sx={{fontSize: 13.5, color: Grey17}}>
                                {one.Name}
                            </Typography>
                            <Typography sx={{fontSize: 12, color: Grey7}}>
                                {one.Email}{one.CompanyName ? ` · ${one.CompanyName}` : ""}
                                {/* THREE SEPARATE WAYS A CONTACT CAN BE UNREACHABLE, shown rather
                                    than hidden: somebody who cannot find a contact they know
                                    exists asks for a duplicate, and then one person has two
                                    identities and an access record split across both. */}
                                {one.Enabled === false ? " · disabled" : ""}
                                {one.Locked ? " · locked out" : ""}
                                {one.HasSignedIn === false ? " · has never signed in" : ""}
                            </Typography>
                        </Box>
                    )}
                    renderInput={params => <TextField {...params} label="Customer contact"
                                                      size="small" margin="dense"/>}/>

                <Box sx={{display: "flex", gap: "10px", marginTop: "8px"}}>
                    <TextField label="Quantity shipped" size="small" value={quantity}
                               onChange={event => setQuantity(event.target.value)}
                               sx={{flex: 1}}/>
                    <TextField label="Unit" size="small" value={unit}
                               onChange={event => setUnit(event.target.value)}
                               sx={{flex: 1}}/>
                </Box>
                <Typography sx={{fontSize: 11.5, color: Grey5, marginTop: "4px"}}>
                    What went on the delivery note, not the lot's balance. A quantity needs its unit.
                </Typography>

                <Box sx={{display: "flex", gap: "10px", marginTop: "12px"}}>
                    <TextField label="Shipment reference" size="small" value={reference}
                               onChange={event => setReference(event.target.value)}
                               sx={{flex: 1}}/>
                    <TextField label="Shipped" type="date" size="small" value={shipped}
                               onChange={event => setShipped(event.target.value)}
                               slotProps={{inputLabel: {shrink: true}}} sx={{flex: 1}}/>
                </Box>

                <Divider sx={{margin: "16px 0", borderColor: Grey2}}/>
                <Typography sx={{fontSize: 13, fontWeight: 600, color: Grey8}}>
                    Documents to release
                </Typography>
                {attachments.length === 0 ? (
                    <Typography sx={{fontSize: 12.5, color: Grey7, marginTop: "6px",
                                     lineHeight: 1.6}}>
                        This lot carries no files. Attach the certificate on the Attachments tab
                        first, then release it.
                    </Typography>
                ) : (
                    // A COLUMN OF ROWS, not `display: block` on each label. That shorthand for
                    // "one per line" overrides the flex layout MUI gives a FormControlLabel, and
                    // every filename wrapped onto the line BELOW its own checkbox -- a column of
                    // loose boxes with captions underneath, ambiguous about which belonged to
                    // which. Caught by looking at the dialog rather than by the typechecker.
                    <Box sx={{display: "flex", flexDirection: "column", marginTop: "2px"}}>
                        {attachments.map(one => (
                            <FormControlLabel key={one.AttachmentUSID}
                                sx={{alignItems: "flex-start", marginLeft: 0, marginRight: 0}}
                                control={<Checkbox size="small" sx={{paddingTop: "4px"}}
                                                   checked={!!chosen[one.AttachmentUSID]}
                                                   onChange={event => setChosen(
                                                       {...chosen,
                                                        [one.AttachmentUSID]:
                                                            event.target.checked})}/>}
                                label={<Typography sx={{fontSize: 13, color: Grey17,
                                                        paddingTop: "7px"}}>
                                    {one.Name}
                                    <Box component="span" sx={{color: Grey5}}>
                                        {one.Bytes ? ` · ${size(one.Bytes)}` : ""}
                                        {one.Missing ? " · no longer in storage" : ""}
                                    </Box>
                                </Typography>}/>
                        ))}
                    </Box>
                )}
                {/* THE SENTENCE THAT STOPS A SURPRISE IN BOTH DIRECTIONS: nothing is shared that
                    was not ticked, and nothing ticked today covers a file added tomorrow. */}
                <Typography sx={{fontSize: 11.5, color: Grey5, marginTop: "6px", lineHeight: 1.6}}>
                    Only the files you tick are shared. A file attached to this lot later is not
                    shared until you release it again — including a corrected certificate.
                </Typography>

                <Divider sx={{margin: "16px 0", borderColor: Grey2}}/>
                <FormControlLabel
                    control={<Checkbox size="small" checked={genealogy}
                                       onChange={event => setGenealogy(event.target.checked)}/>}
                    label={<Typography sx={{fontSize: 13, color: Grey17}}>
                        Also show them what this lot was made from
                    </Typography>}/>
                {/* WHAT THE TICK ACTUALLY DISCLOSES, and what it never does. */}
                <Typography sx={{fontSize: 11.5, color: Grey5, lineHeight: 1.6}}>
                    They will see the lot numbers of the batches consumed into this one, three
                    generations back. Your suppliers are never named, with or without this.
                </Typography>

                <Box sx={{display: "flex", gap: "10px", marginTop: "14px", alignItems: "center"}}>
                    <TextField label="Access expires" type="date" size="small" value={expires}
                               disabled={neverExpires}
                               onChange={event => setExpires(event.target.value)}
                               slotProps={{inputLabel: {shrink: true}}} sx={{flex: 1}}/>
                    <FormControlLabel
                        control={<Checkbox size="small" checked={neverExpires}
                                           onChange={event =>
                                               setNeverExpires(event.target.checked)}/>}
                        label={<Typography sx={{fontSize: 12.5, color: Grey7}}>Never</Typography>}/>
                </Box>
                <Typography sx={{fontSize: 11.5, color: Grey5, marginTop: "4px"}}>
                    Left empty, access runs for two years — long enough that a certificate does not
                    go dark while the material is still in service.
                </Typography>

                {problem && (
                    <Typography sx={{fontSize: 13, color: "#B3261E", marginTop: "12px"}}>
                        {problem}
                    </Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} sx={{textTransform: "none"}}>Cancel</Button>
                <Button variant="contained" disabled={!recipient || saving} onClick={release}
                        sx={{textTransform: "none"}}>
                    {saving ? "Releasing…" : "Release"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
